import React, { useMemo, useState } from 'react';
import { FinancialSummaryMath } from '../../FinancialSummaryMath/FinancialSummaryMath';
import CurrencyInput from '../../CurrencyInput/components/CurrencyInput';
import ProFormaScenarioTable from './ProFormaScenarioTable';
import { isPoolPreviewActive, buildPoolEngineOffer } from '../utils/buildPoolEngineInput';
import { SIBLING_ROW_PREFIX } from '../utils/siblingGuaranteeExpenses';

const MULTIPLIERS = [0.7, 0.8, 0.9, 1.0];
const LABELS = ['70%', '80%', '90%', '100%'];

const withVenueOnlyForecastAsOffer = (item) => {
  if (item.show_in_offer ?? true) return item;

  return { ...item, ...FinancialSummaryMath.forecastAsOfferInputs(item) };
};

// In Independent mode the budget passed in is V3FinancePage's `budgetForMath`,
// which prepends sibling V3Offer guarantees to expense_line_items (O-2993), so
// scenarios reflect the full venue cost. In Pool mode V3FinancePage passes the
// sibling-free `budgetForComponents` and we run the POOL engine offer (the
// shared deal + member splits) instead of the raw offer — otherwise every
// scenario's artist payout / venue net would ignore the pool (the offer's own
// guarantee is $0 in pool mode). This mirrors useV3OfferBudget's `calculated`.
export const generateScenarios = (offer, budgetForComponents) => {
  const engineOffer = isPoolPreviewActive(offer, offer?.lineup)
    ? buildPoolEngineOffer(offer, offer.lineup)
    : offer;

  // Independent mode prepends sibling artists' guarantees into
  // expense_line_items (O-2783). Surface them on the Artist Payout line as the
  // COMBINED artist cost rather than burying them in Expenses. They're fixed
  // rows, so the total is constant across the ticket-scaling scenarios. (In
  // pool mode the budget is sibling-free and the engine offer already returns
  // the combined pool payout, so this is 0.)
  const siblingArtistCost = (budgetForComponents.expense_line_items || [])
    .filter((it) => String(it.uuid || it.id || '').startsWith(SIBLING_ROW_PREFIX) || it.isSiblingGuarantee)
    .reduce((sum, it) => sum + (parseFloat(it.left_hand) || 0) * (parseFloat(it.right_hand) || 1), 0);

  const incomeLineItems = (budgetForComponents.income_line_items || []).map(withVenueOnlyForecastAsOffer);
  const expenseLineItems = (budgetForComponents.expense_line_items || []).map(withVenueOnlyForecastAsOffer);

  return MULTIPLIERS.map((mult, i) => {
    const scaledBudget = {
      ...budgetForComponents,
      ticket_line_items: (budgetForComponents.ticket_line_items || []).map(t => ({
        ...t,
        right_hand: Math.round(parseFloat(t.right_hand || 0) * mult),
      })),
      income_line_items: incomeLineItems,
      expense_line_items: expenseLineItems,
    };

    try {
      const calculator = new FinancialSummaryMath(engineOffer, scaledBudget, false);
      const calc = JSON.parse(calculator.calculate());

      const totalAttendance = (calc.ticketsSection?.items || [])
        .filter(t => t.show_in_offer !== false && t.calculatedLeftHand > 0)
        .reduce((sum, t) => sum + (t.calculatedRightHand || 0), 0);

      return {
        label: LABELS[i],
        isExpected: false,
        attendance: totalAttendance,
        grossPotential: (calc.ticketsSection?.externalTotal || 0) + (calc.revenueSection?.externalTotal || 0),
        ancillaryIncome: calc.showSummary?.ancillaryIncome || 0,
        totalRevenue: (calc.ticketsSection?.externalTotal || 0) +
          (calc.revenueSection?.externalTotal || 0) +
          (calc.showSummary?.ancillaryIncome || 0),
        beforeAdjustedDeductions: calc.beforeAdjustedDeductionsSection?.externalTotal || 0,
        artistPayout: (calc.artistEarnings?.artistTotalDue || 0) + siblingArtistCost,
        expenses: (calc.expensesSection?.externalTotal || 0)
          + (calc.finalExpensesSection?.externalTotal || 0)
          - siblingArtistCost,
        ancillaryExpenses: calc.showSummary?.ancillaryExpenses || 0,
        promoterProfit: calc.showSummary?.promoterProfit || 0,
        showProfit: calc.showSummary?.venueNetPotential || 0,
      };
    } catch (e) {
      console.error('Pro-forma scenario calculation failed:', e);
      return {
        label: LABELS[i],
        isExpected: false,
        attendance: 0,
        grossPotential: 0,
        ancillaryIncome: 0,
        totalRevenue: 0,
        beforeAdjustedDeductions: 0,
        artistPayout: 0,
        expenses: 0,
        ancillaryExpenses: 0,
        promoterProfit: 0,
        showProfit: 0,
      };
    }
  });
};

const ProFormaModal = ({ team, offer, budgetForComponents, onClose }) => {
  const [cmTarget, setCmTarget] = useState(0);

  const scenarios = useMemo(() => {
    if (!offer || !budgetForComponents) return [];
    return generateScenarios(offer, budgetForComponents);
  }, [offer, budgetForComponents]);

  return (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px 24px',
        borderBottom: '1px solid var(--bk-c-e9eaeb, #E9EAEB)'
      }}>
        <h5 style={{ margin: 0, fontWeight: 600, fontSize: '18px' }}>Pro-Forma</h5>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: '20px',
            color: 'var(--bk-c-717680, #717680)',
            padding: '4px 8px',
            lineHeight: 1
          }}
        >
          &times;
        </button>
      </div>
      <div style={{ padding: '20px 24px' }}>
        <div style={{ marginBottom: '20px' }}>
          <label
            style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: 500,
              color: 'var(--bk-c-535862, #535862)',
              marginBottom: '6px'
            }}
          >
            CM Target (per show)
          </label>
          <div style={{ width: '200px' }}>
            <CurrencyInput
              className="form-control"
              value={cmTarget || ''}
              onChange={(e) => {
                const raw = e.target.value.replace(/[$,]/g, '');
                setCmTarget(parseFloat(raw) || 0);
              }}
              placeholder="$0"
              style={{ fontSize: '14px' }}
              maskOptions={{ allowDecimal: false }}
            />
          </div>
        </div>
        <ProFormaScenarioTable scenarios={scenarios} cmTarget={cmTarget} />
      </div>
    </div>
  );
};

export default ProFormaModal;
